import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  Card,
  Divider,
} from "@mui/material";
import { FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import { MdSubscriptions } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";

export default function SubscriptionDetails() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [schoolName, setSchoolName] = useState("");
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [payData, setPayData] = useState({
    subscription_id: "",
    school_id: schoolId,
    amount: "",
    payment_mode: "UPI",
    utr_number: "",
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const [subscriptionResponse, schoolsResponse] = await Promise.all([
        api.get(`/super_admin/subscriptions/list?school_id=${schoolId}`),
        api.get("/super_admin/school/list"),
      ]);
      setSubs(subscriptionResponse.data?.data || []);
      const school = (schoolsResponse.data?.data || []).find(
        (item) => String(item.id) === String(schoolId)
      );
      setSchoolName(school?.name || "School Subscriptions");
    } catch (err) {
      console.error("Fetch subscription details error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [schoolId]);

  const openPay = (sub) => {
    setPayData({
      subscription_id: sub.id,
      school_id: schoolId,
      amount: sub.amount,
      payment_mode: "UPI",
      utr_number: "",
    });
    setOpenPayDialog(true);
  };

  const handlePaySave = async () => {
    try {
      const res = await api.post("/super_admin/subscriptions/pay", payData, {
        headers: { "Content-Type": "application/json" },
      });
      setSnackbar({ open: true, message: res.data.message, severity: res.data.status ? "success" : "error" });
      setOpenPayDialog(false);
      fetchDetails();
    } catch {
      setSnackbar({ open: true, message: "Payment failed", severity: "error" });
    }
  };

  return (
    <Card sx={{ p: 3, borderRadius: 3, boxShadow: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <MdSubscriptions size={28} color="#1976d2" />
          <Typography variant="h5" fontWeight={800} sx={{ overflowWrap: "anywhere" }}>
            {schoolName}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FaArrowLeft />}
          onClick={() => navigate("/super_admin/subscriptions")}
          sx={{ flexShrink: 0, borderRadius: 2, textTransform: "none" }}
        >
          Back
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Fetching subscriptions...
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 2, overflowX: "auto" }}>
          <Table>
            <TableHead sx={{ bgcolor: "#f1f5f9" }}>
              <TableRow>
                <TableCell><b>Month</b></TableCell>
                <TableCell><b>Amount</b></TableCell>
                <TableCell><b>Due Date</b></TableCell>
                <TableCell><b>Paid Date</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Mode</b></TableCell>
                <TableCell><b>Txn ID</b></TableCell>
                <TableCell align="right"><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subs.map((sub) => (
                <TableRow key={sub.id} hover>
                  <TableCell>{sub.month}</TableCell>
                  <TableCell>₹ {sub.amount}</TableCell>
                  <TableCell>{sub.due_date}</TableCell>
                  <TableCell>{sub.paid_date || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={sub.status.toUpperCase()}
                      size="small"
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        background:
                          sub.status === "paid"
                            ? "linear-gradient(45deg,#2e7d32,#66bb6a)"
                            : sub.status === "pending"
                            ? "linear-gradient(45deg,#f57c00,#ffb74d)"
                            : "linear-gradient(45deg,#c62828,#ef5350)",
                      }}
                    />
                  </TableCell>
                  <TableCell>{sub.payment_mode || "-"}</TableCell>
                  <TableCell>{sub.transaction_id || "-"}</TableCell>
                  <TableCell align="right">
                    {sub.status !== "paid" && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openPay(sub)}
                        sx={{ textTransform: "none", borderRadius: 2 }}
                      >
                        Pay Now
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!subs.length && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                    <Typography variant="h6" color="text.secondary">
                      No subscriptions found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openPayDialog} onClose={() => setOpenPayDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>💳 Pay Subscription</DialogTitle>
        <Divider />
        <DialogContent sx={{ mt: 2 }}>
          <TextField margin="dense" label="Amount" fullWidth value={payData.amount} InputProps={{ readOnly: true }} />
          <TextField
            margin="dense"
            select
            label="Payment Mode"
            fullWidth
            value={payData.payment_mode}
            onChange={(e) => setPayData((current) => ({ ...current, payment_mode: e.target.value }))}
          >
            <MenuItem value="UPI">UPI</MenuItem>
            <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
            <MenuItem value="Online">Online</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="UTR Number"
            fullWidth
            value={payData.utr_number}
            onChange={(e) => setPayData((current) => ({ ...current, utr_number: e.target.value }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenPayDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePaySave}
            startIcon={<FaCheckCircle />}
            sx={{ background: "linear-gradient(45deg,#2e7d32,#66bb6a)", borderRadius: 2, textTransform: "none" }}
          >
            Confirm Pay
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
}
